import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Button,
  Alert,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  Article as ArticleIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Category as CategoryIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import { fetchGroupedNews, GroupedNews } from "../../services/api";
import PageHeader from "../common/PageHeader";
import EmptyState from "../common/EmptyState";

const GroupedNewsList: React.FC = () => {
  const [expandedCategories, setExpandedCategories] = useState<
    Record<number, boolean>
  >({});

  const {
    data: groupedNews,
    isLoading,
    error,
    refetch,
  } = useQuery<GroupedNews[]>({
    queryKey: ["groupedNews"],
    queryFn: fetchGroupedNews,
  });

  const handleCategoryToggle = (categoryId: number) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  const getTotalNewsCount = () => {
    return (
      groupedNews?.reduce((total, group) => total + group.news.length, 0) || 0
    );
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={4}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Error loading grouped news. Please try again.
        </Alert>
        <Button variant="outlined" onClick={() => refetch()}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <>
      <PageHeader
        title="News by Category"
        breadcrumbs={[
          { label: "Dashboard", path: "/" },
          { label: "News", path: "/" },
          { label: "Grouped View" },
        ]}
        action={
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              component={Link}
              to="/news/categories"
              variant="outlined"
              startIcon={<CategoryIcon />}
            >
              Manage Categories
            </Button>
            <Button
              component={Link}
              to="/news/new"
              variant="contained"
              startIcon={<AddIcon />}
            >
              Add News Article
            </Button>
          </Box>
        }
      />

      <Card sx={{ mb: 3, p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Summary
        </Typography>
        <Box sx={{ display: "flex", gap: 3, alignItems: "center" }}>
          <Box>
            <Typography variant="h4" color="primary">
              {groupedNews?.length || 0}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Categories
            </Typography>
          </Box>
          <Box>
            <Typography variant="h4" color="secondary">
              {getTotalNewsCount()}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Total Articles
            </Typography>
          </Box>
        </Box>
      </Card>

      {!groupedNews || groupedNews.length === 0 ? (
        <EmptyState
          title="No Categorized News Found"
          message="No news articles have been categorized yet. Create some categories and articles to see them grouped here."
          icon={<CategoryIcon />}
          action={{
            label: "Manage Categories",
            onClick: () => (window.location.href = "/news/categories"),
          }}
        />
      ) : (
        <Box sx={{ space: 2 }}>
          {groupedNews.map((group) => (
            <Accordion
              key={group.categoryId}
              expanded={expandedCategories[group.categoryId] || false}
              onChange={() => handleCategoryToggle(group.categoryId)}
              sx={{ mb: 2, borderRadius: 2, "&:before": { display: "none" } }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  backgroundColor: "background.paper",
                  borderRadius: 2,
                  "&.Mui-expanded": {
                    borderBottomLeftRadius: 0,
                    borderBottomRightRadius: 0,
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    width: "100%",
                  }}
                >
                  <CategoryIcon color="primary" />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6">{group.title}</Typography>
                    {group.description && (
                      <Typography variant="body2" color="text.secondary">
                        {group.description}
                      </Typography>
                    )}
                  </Box>
                  <Chip
                    label={`${group.news.length} ${
                      group.news.length === 1 ? "article" : "articles"
                    }`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                {group.news.length === 0 ? (
                  <Box sx={{ p: 3, textAlign: "center" }}>
                    <Typography color="text.secondary">
                      No articles in this category yet.
                    </Typography>
                  </Box>
                ) : (
                  <List disablePadding>
                    {group.news.map((article, index) => (
                      <React.Fragment key={article.id}>
                        <ListItem sx={{ py: 2, px: 3 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 2,
                              width: "100%",
                            }}
                          >
                            <ArticleIcon
                              color={article.active ? "primary" : "disabled"}
                              sx={{ mt: 0.5 }}
                            />
                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                              <Typography
                                variant="subtitle1"
                                fontWeight="medium"
                                sx={{
                                  color: article.active
                                    ? "text.primary"
                                    : "text.disabled",
                                  wordBreak: "break-word",
                                }}
                              >
                                {article.title}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  mt: 0.5,
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                }}
                              >
                                {article.shortDescription}
                              </Typography>
                              <Box sx={{ mt: 1 }}>
                                <Chip
                                  label={article.active ? "Active" : "Inactive"}
                                  size="small"
                                  color={article.active ? "success" : "default"}
                                  variant="outlined"
                                />
                              </Box>
                            </Box>
                          </Box>
                          <ListItemSecondaryAction>
                            <Box sx={{ display: "flex", gap: 0.5 }}>
                              <Tooltip title="View Details">
                                <IconButton
                                  component={Link}
                                  to={`/news/view/${article.id}`}
                                  size="small"
                                  color="info"
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Edit Article">
                                <IconButton
                                  component={Link}
                                  to={`/news/edit/${article.id}`}
                                  size="small"
                                  color="primary"
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </ListItemSecondaryAction>
                        </ListItem>
                        {index < group.news.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}
    </>
  );
};

export default GroupedNewsList;
